import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import TopicAside from "./components/TopicAside";
import TopicDiscussion from "./components/TopicDiscussion";
import { topics } from "@/mocks/topics";
import { categories } from "@/mocks/categories";
import { usePageMeta } from "@/hooks/usePageMeta";
import { formatDate, formatViews } from "@/utils/format";

export default function TopicPage() {
  const { id = "" } = useParams();
  const topic = topics.find((item) => item.id === id);
  const category = categories.find((item) => item.slug === topic?.category);

  usePageMeta({
    title: topic
      ? `${topic.title} | 黑瓜吃料瓜田讨论区`
      : "话题未找到 | 黑瓜吃料瓜田讨论区",
    description: topic
      ? `${topic.excerpt}（黑瓜吃料 · ${topic.categoryName}讨论区，共 ${topic.comments} 条围观群众观点）`
      : "该话题可能已经下架或链接有误，返回黑瓜吃料首页查看更多热门瓜田话题。",
    keywords: topic
      ? `黑瓜吃料,${topic.categoryName},${topic.tags.join(",")},瓜田讨论区,围观群众热议`
      : "黑瓜吃料,瓜田讨论区,围观群众热议",
    path: `/topic/${id}`,
    type: "article",
    jsonLd: topic
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: topic.title,
            description: topic.excerpt,
            articleSection: topic.categoryName,
            inLanguage: "zh-CN",
            datePublished: topic.publishedAt,
            dateModified: topic.publishedAt,
            keywords: topic.tags.join(","),
            author: {
              "@type": "Organization",
              name: "黑瓜吃料",
            },
            publisher: {
              "@type": "Organization",
              name: "黑瓜吃料",
            },
            mainEntityOfPage: `https://geofix.cn/topic/${topic.id}`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://geofix.cn/" },
              {
                "@type": "ListItem",
                position: 2,
                name: topic.categoryName,
                item: `https://geofix.cn/category/${topic.category}`,
              },
              {
                "@type": "ListItem",
                position: 3,
                name: topic.title,
                item: `https://geofix.cn/topic/${topic.id}`,
              },
            ],
          },
        ]
      : undefined,
  });

  if (!topic) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <SiteHeader />
        <main className="flex flex-1 items-center justify-center px-4 py-20">
          <div className="flex max-w-md flex-col items-center text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-emotion-sad-line text-3xl" />
            </span>
            <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
              这个瓜好像不在了
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              话题可能已下架，或者链接有误。回首页看看今天大家在聊什么吧。
            </p>
            <Link
              to="/"
              className="mt-6 flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-home-4-line text-base" />
              </span>
              返回首页
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <article>
          <header className="w-full border-b border-background-200 bg-background-100 py-8 md:py-10">
            <div className="mx-auto max-w-[1120px] px-4 md:px-6">
              <Breadcrumbs
                items={[
                  { label: topic.categoryName, to: `/category/${topic.category}` },
                  { label: topic.title },
                ]}
              />
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to={`/category/${topic.category}`}
                  className="flex items-center gap-1.5 rounded-md bg-accent-100 px-2.5 py-1 text-xs font-medium text-accent-800 transition-colors hover:bg-accent-200"
                >
                  <span className="flex h-3.5 w-3.5 items-center justify-center">
                    <i className={`${category?.icon ?? "ri-layout-grid-line"} text-sm`} />
                  </span>
                  {topic.categoryName}
                </Link>
                <span className="flex items-center gap-1.5 rounded-md bg-primary-100 px-2.5 py-1 text-xs font-medium text-primary-700">
                  <span className="flex h-3.5 w-3.5 items-center justify-center">
                    <i className="ri-fire-fill text-sm" />
                  </span>
                  热度 {topic.heat}
                </span>
              </div>
              <h1 className="mt-4 max-w-3xl font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
                {topic.title}
              </h1>
              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500 md:text-sm">
                <span className="flex items-center gap-1.5">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-user-3-line text-base" />
                  </span>
                  {topic.author}
                  <span className="text-foreground-400">· {topic.authorRole}</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-calendar-line text-base" />
                  </span>
                  {formatDate(topic.publishedAt)}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-eye-line text-base" />
                  </span>
                  {formatViews(topic.views)} 阅读
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-time-line text-base" />
                  </span>
                  阅读约 {topic.readMinutes} 分钟
                </span>
              </div>
            </div>
          </header>

          <div className="mx-auto max-w-[1120px] px-4 md:px-6">
            <div className="mt-8 h-[240px] w-full overflow-hidden rounded-card border border-background-200 bg-background-200 md:h-[400px]">
              <img
                src={topic.cover}
                alt={topic.coverAlt}
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>

          <div className="mx-auto grid max-w-[1120px] grid-cols-1 gap-8 px-4 py-10 md:px-6 md:py-14 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <p className="border-l-4 border-primary-400 bg-primary-50 px-4 py-3 text-sm leading-relaxed text-foreground-700 md:text-base">
                {topic.excerpt}
              </p>

              <div className="mt-6 rounded-card border border-accent-200 bg-accent-50 p-5">
                <h2 className="flex items-center gap-2 text-sm font-semibold text-accent-900">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-flashlight-line text-base" />
                  </span>
                  讨论焦点
                </h2>
                <ul className="mt-3 flex flex-col gap-2.5">
                  {topic.highlights.map((point) => (
                    <li key={point} className="flex items-start gap-2 text-sm text-foreground-700">
                      <span className="mt-1 flex h-3.5 w-3.5 shrink-0 items-center justify-center text-accent-600">
                        <i className="ri-checkbox-blank-circle-fill text-[8px]" />
                      </span>
                      {point}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-7 flex flex-col gap-5">
                {topic.content.map((paragraph, index) => (
                  <p
                    key={`p-${index}`}
                    className="text-[15px] leading-loose text-foreground-800 md:text-base"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
                <span className="text-sm text-foreground-500">话题标签：</span>
                {topic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-md bg-secondary-100 px-2.5 py-1.5 text-xs text-secondary-900"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <TopicDiscussion
                key={topic.id}
                topicTitle={topic.title}
                initialItems={topic.discussion}
              />
            </div>

            <div className="lg:col-span-4">
              <div className="lg:sticky lg:top-24">
                <TopicAside topic={topic} />
              </div>
            </div>
          </div>
        </article>

        <section className="w-full border-t border-background-200 bg-background-100 py-10 md:py-14">
          <div className="mx-auto max-w-[1120px] px-4 md:px-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                <span className="flex h-5 w-5 items-center justify-center text-accent-600">
                  <i className="ri-compasses-2-line text-lg" />
                </span>
                继续逛瓜田
              </h2>
              <Link
                to={`/category/${topic.category}`}
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                更多「{topic.categoryName}」话题
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-arrow-right-line text-base" />
                </span>
              </Link>
            </div>
            <ul className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {topics
                .filter((item) => item.id !== topic.id)
                .slice(0, 3)
                .map((item) => (
                  <li key={item.id}>
                    <Link
                      to={`/topic/${item.id}`}
                      className="group flex h-full flex-col rounded-card border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
                    >
                      <span className="text-xs text-foreground-500">{item.categoryName}</span>
                      <h3 className="mt-2 line-clamp-2 flex-1 text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
                        {item.title}
                      </h3>
                      <span className="mt-4 flex items-center gap-2 text-xs text-foreground-500">
                        <span className="flex items-center gap-1">
                          <span className="flex h-3.5 w-3.5 items-center justify-center">
                            <i className="ri-chat-3-line text-sm" />
                          </span>
                          {item.comments}
                        </span>
                        <span className="text-foreground-300">·</span>
                        <span>{formatViews(item.views)} 阅读</span>
                      </span>
                    </Link>
                  </li>
                ))}
            </ul>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}